import React, { useEffect, useRef, useState } from 'react';
import { Pen, RotateCcw } from 'lucide-react';

/**
 * Touch + mouse signature pad. Returns a base64 PNG via onChange.
 * Props: onChange(dataUrl|null), height, label
 *
 * IMPORTANT: We attach touch handlers manually via addEventListener with
 * { passive: false } to keep e.preventDefault() effective on mobile.
 * React's JSX onTouch* are passive by default since React 17, which means
 * preventDefault() is ignored and the page scrolls while signing.
 */
export function SignaturePad({ onChange, height = 200, label }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const hasDrawnRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0F1F33';
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const getPoint = (e) => {
      const rect = canvas.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: t.clientX - rect.left, y: t.clientY - rect.top };
    };

    const start = (e) => {
      // Prevent page scroll/zoom on mobile while signing
      if (e.cancelable) e.preventDefault();
      drawingRef.current = true;
      lastPointRef.current = getPoint(e);
    };

    const move = (e) => {
      if (!drawingRef.current) return;
      if (e.cancelable) e.preventDefault();
      const ctx = canvas.getContext('2d');
      const p = getPoint(e);
      ctx.beginPath();
      ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      lastPointRef.current = p;
      if (!hasDrawnRef.current) {
        hasDrawnRef.current = true;
        setIsEmpty(false);
      }
    };

    const end = (e) => {
      if (!drawingRef.current) return;
      if (e && e.cancelable) e.preventDefault();
      drawingRef.current = false;
      const dataUrl = canvas.toDataURL('image/png');
      onChangeRef.current?.(hasDrawnRef.current ? dataUrl : null);
    };

    // Mouse handlers (passive by default — fine)
    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', end);
    canvas.addEventListener('mouseleave', end);

    // Touch handlers MUST be non-passive to allow preventDefault()
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end, { passive: false });
    canvas.addEventListener('touchcancel', end, { passive: false });

    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      canvas.removeEventListener('mouseup', end);
      canvas.removeEventListener('mouseleave', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
      canvas.removeEventListener('touchcancel', end);
    };
  }, []);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasDrawnRef.current = false;
    setIsEmpty(true);
    onChangeRef.current?.(null);
  };

  return (
    <div className="space-y-2">
      {label && <label className="label">{label}</label>}
      <div className="rounded-xl border-2 border-[#E5DCC9] bg-[#FAF7F2] overflow-hidden relative">
        <canvas
          ref={canvasRef}
          data-testid="signature-canvas"
          style={{
            width: '100%',
            height: `${height}px`,
            touchAction: 'none',
            overscrollBehavior: 'contain',
            WebkitUserSelect: 'none',
            userSelect: 'none',
            display: 'block',
            cursor: 'crosshair',
          }}
        />
        {isEmpty && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-gray-400 gap-2">
            <Pen size={16} /> <span className="text-sm">Signer ici</span>
          </div>
        )}
      </div>
      <button
        type="button"
        data-testid="signature-clear"
        onClick={clear}
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-bold text-gray-600 hover:bg-gray-100"
      >
        <RotateCcw size={13} /> Effacer
      </button>
    </div>
  );
}
